import * as CL from '@design-system/component-library';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import {
  cancelMsg,
  companyNameMsg,
  confirmMsg,
  confirmNumberPublicityDetailsMsg,
  editMsg,
  fieldCantBeEmptyMsg,
  nameMsg,
  postOfficeMsg,
  postalCodeMsg,
  streetAddressMsg,
  t,
} from '../../../../common/i18n';
import { scrollTo } from '../../../../common/utils/browserUtils.js';
import { POSTAL_CODE_REGEX } from '../../../../common/utils/validationUtils.js';
import { Input } from '../../../Input/Input.js';
import { ValidationMessage } from '../../../ValidationMessage/ValidationMessage';
import type { NumberPublicityAddressDetailsCoCo } from '../../placeOrderTypesCoCo.js';

export interface NumberPrivacyCustomDetailsProps {
  isCustomSelected: boolean;
  fieldName: string;
  defaultValue?: string;
  companyName?: string;
  onConfirm?: (details: NumberPublicityAddressDetailsCoCo) => void;
  initialConfirmedDetails?: NumberPublicityAddressDetailsCoCo;
}

export interface NumberPrivacyCustomDetailsHandle {
  validate: () => Promise<boolean>;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

type FieldKey = 'companyName' | 'recipientName' | 'line' | 'postalCode' | 'postOffice';
const FIELD_KEYS: FieldKey[] = ['companyName', 'recipientName', 'line', 'postalCode', 'postOffice'];

interface CustomDirectoryDetailsSnapshot {
  companyName: string;
  recipientName: string;
  line: string;
  postalCode: string;
  postOffice: string;
}

const toNumberPublicityDetails = (snapshot: CustomDirectoryDetailsSnapshot): NumberPublicityAddressDetailsCoCo => ({
  companyName: snapshot.companyName,
  recipientName: snapshot.recipientName,
  address: {
    line: snapshot.line,
    postalCode: snapshot.postalCode,
    postOffice: snapshot.postOffice,
  },
});

const toSnapshot = (details?: NumberPublicityAddressDetailsCoCo): CustomDirectoryDetailsSnapshot => ({
  companyName: details?.companyName ?? '',
  recipientName: details?.recipientName ?? '',
  line: details?.address?.line ?? '',
  postalCode: details?.address?.postalCode ?? '',
  postOffice: details?.address?.postOffice ?? '',
});

const requiredMsg = () => t.VPVR(fieldCantBeEmptyMsg);

export const NumberPublicityCustomDetailsCoCo = forwardRef<
  NumberPrivacyCustomDetailsHandle,
  NumberPrivacyCustomDetailsProps
>(({ isCustomSelected, fieldName, defaultValue, companyName, onConfirm, initialConfirmedDetails }, ref) => {
  const { control, setValue: setRadioValue, getValues, trigger, getFieldState, formState } = useFormContext();

  const detailsFieldName = `${fieldName}Details`;
  const fieldPaths: Record<FieldKey, string> = {
    companyName: `${detailsFieldName}.companyName`,
    recipientName: `${detailsFieldName}.recipientName`,
    line: `${detailsFieldName}.line`,
    postalCode: `${detailsFieldName}.postalCode`,
    postOffice: `${detailsFieldName}.postOffice`,
  };

  const [localConfirmedDetails, setLocalConfirmedDetails] = useState<CustomDirectoryDetailsSnapshot | undefined>(
    undefined
  );
  const [isEditingOverride, setIsEditingOverride] = useState(false);
  const [lastConfirmedRadioValue, setLastConfirmedRadioValue] = useState<string | undefined>(undefined);
  const [hasBlockingError, setHasBlockingError] = useState(false);

  const effectiveConfirmedDetails: CustomDirectoryDetailsSnapshot | undefined =
    localConfirmedDetails ?? (initialConfirmedDetails ? toSnapshot(initialConfirmedDetails) : undefined);
  const isConfirmed = !isEditingOverride && !!effectiveConfirmedDetails;
  const showForm = isCustomSelected && !isConfirmed;

  const fieldDefaults: CustomDirectoryDetailsSnapshot = effectiveConfirmedDetails ?? {
    companyName: companyName ?? '',
    recipientName: '',
    line: '',
    postalCode: '',
    postOffice: '',
  };

  const fieldIdPrefix = `${fieldName}-custom-details`;
  const confirmButtonId = `${fieldIdPrefix}-confirm-button`;
  const fieldIds: Record<FieldKey, string> = {
    companyName: `${fieldIdPrefix}-companyName`,
    recipientName: `${fieldIdPrefix}-recipientName`,
    line: `${fieldIdPrefix}-line`,
    postalCode: `${fieldIdPrefix}-postalCode`,
    postOffice: `${fieldIdPrefix}-postOffice`,
  };

  const fieldLabels: Record<FieldKey, string> = {
    companyName: t.AJ93(companyNameMsg),
    recipientName: t.PNS1(nameMsg),
    line: t.DD38(streetAddressMsg),
    postalCode: t.RUAW(postalCodeMsg),
    postOffice: t.Z7S5(postOfficeMsg),
  };

  const confirmCustomDetails = async (): Promise<boolean> => {
    const isValid = await trigger(FIELD_KEYS.map(key => fieldPaths[key]));
    if (isValid) {
      const snapshot: CustomDirectoryDetailsSnapshot = {
        companyName: getValues(fieldPaths.companyName) ?? '',
        recipientName: getValues(fieldPaths.recipientName) ?? '',
        line: getValues(fieldPaths.line) ?? '',
        postalCode: getValues(fieldPaths.postalCode) ?? '',
        postOffice: getValues(fieldPaths.postOffice) ?? '',
      };
      setLocalConfirmedDetails(snapshot);
      setIsEditingOverride(false);
      setLastConfirmedRadioValue(getValues(fieldName));
      onConfirm?.(toNumberPublicityDetails(snapshot));
    }
    return isValid;
  };

  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        if (!isCustomSelected) {
          setHasBlockingError(false);
          return true;
        }
        if (isConfirmed) {
          setHasBlockingError(false);
          return true;
        }
        await trigger(FIELD_KEYS.map(key => fieldPaths[key]));
        setHasBlockingError(true);
        return false;
      },
      scrollToFirstError: () => {
        for (const key of FIELD_KEYS) {
          const state = getFieldState(fieldPaths[key], formState);
          if (!state.error) {
            continue;
          }
          const element = document.querySelector<HTMLInputElement>(`input[name="${fieldIds[key]}"]`);
          if (element) {
            scrollTo(element);
            element.focus();
          }
          return;
        }
        const confirmButton = document.getElementById(confirmButtonId);
        if (confirmButton) {
          scrollTo(confirmButton);
        }
      },
      getErrorCount: () => (hasBlockingError ? 1 : 0),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCustomSelected, isConfirmed, hasBlockingError]
  );

  if (!isCustomSelected) {
    return null;
  }

  const handleConfirm = async () => {
    const isValid = await confirmCustomDetails();
    setHasBlockingError(!isValid);
  };

  const handleEdit = () => {
    setIsEditingOverride(true);
  };

  const handleCancel = () => {
    if (effectiveConfirmedDetails) {
      setIsEditingOverride(false);
      setHasBlockingError(false);
      return;
    }
    setRadioValue(fieldName, lastConfirmedRadioValue ?? defaultValue);
    setIsEditingOverride(false);
    setHasBlockingError(false);
  };

  const renderField = (key: FieldKey, extra?: Partial<{ maxLength: number }>) => (
    <Controller
      name={fieldPaths[key]}
      control={control}
      shouldUnregister={true}
      defaultValue={fieldDefaults[key]}
      rules={{
        required: { value: true, message: requiredMsg() },
        ...(key === 'postalCode'
          ? { pattern: { value: POSTAL_CODE_REGEX, message: t.AT96('Invalid postal code') } }
          : {}),
      }}
      render={({ field, fieldState }) => (
        <Input
          id={fieldIds[key]}
          name={fieldIds[key]}
          label={fieldLabels[key]}
          required={true}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
          {...extra}
        />
      )}
    />
  );

  return (
    <>
      {showForm && (
        <div className="of-number-privacy__custom-details">
          <CL.Grid>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                {renderField('companyName')}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                {renderField('recipientName')}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                {renderField('line')}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                {renderField('postalCode', { maxLength: 5 })}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                {renderField('postOffice')}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow>
              <CL.GridCol colWidthXS={4}>
                <CL.Button id={confirmButtonId} type="button" onClick={handleConfirm}>
                  {t.QVYK(confirmMsg)}
                </CL.Button>
                <CL.Button type="button" color="secondary" className={dsClass.MARGIN_LEFT_4} onClick={handleCancel}>
                  {t.B2V1(cancelMsg)}
                </CL.Button>
              </CL.GridCol>
            </CL.GridRow>
          </CL.Grid>
          {hasBlockingError && <ValidationMessage message={t.UK9J(confirmNumberPublicityDetailsMsg)} />}
        </div>
      )}
      {isConfirmed && effectiveConfirmedDetails && (
        <div className="of-number-privacy__custom-summary">
          <CL.Grid>
            {FIELD_KEYS.map(key => (
              <CL.GridRow key={key}>
                <CL.GridCol colWidthXS={4} colWidthS={4} colWidthL={6}>
                  <div className={dsClass.MARGIN_BOTTOM_4}>
                    <p className={dsClass.FONT_WEIGHT_600} style={{ margin: 0 }}>
                      {fieldLabels[key]}
                    </p>
                    <p style={{ margin: 0 }}>{effectiveConfirmedDetails[key]}</p>
                  </div>
                </CL.GridCol>
              </CL.GridRow>
            ))}
            <CL.GridRow>
              <CL.GridCol colWidthXS={4}>
                <CL.Button type="button" color="secondary" onClick={handleEdit}>
                  {t.NVPK(editMsg)}
                </CL.Button>
              </CL.GridCol>
            </CL.GridRow>
          </CL.Grid>
        </div>
      )}
    </>
  );
});

NumberPublicityCustomDetailsCoCo.displayName = 'NumberPublicityCustomDetailsCoCo';
